import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { getDashboardMetrics, getSales } from '../api/client';
import { formatCurrency, formatDualCurrency, formatPercentage, getTrendIcon, formatDate } from '../utils/formatters';
import { useExchangeRate } from '../contexts/ExchangeRateContext';
import { useAuth } from '../contexts/AuthContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend, ReferenceLine } from 'recharts';
import { Navigate } from 'react-router-dom';
import DailySalesChart from './DailySalesChart';
import { useTenant } from '../contexts/TenantContext';

const Dashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { exchangeRate, formatDualCurrency: formatDual } = useExchangeRate();
  const { user, isAdmin } = useAuth();
  const { companyName, logoUrl } = useTenant();

  // Redirect compras users to Inventory
  if (user?.role === 'compras') {
    return <Navigate to="/inventory" replace />;
  }

  // Estado para el modal de ventas
  const [showSalesModal, setShowSalesModal] = useState(false);
  const [sales, setSales] = useState([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesDateRange, setSalesDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    endDate: new Date()
  });

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const data = await getDashboardMetrics();
      setMetrics(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard metrics:', err);
      setError('Error al cargar las métricas del dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchSales = async () => {
    try {
      setSalesLoading(true);
      const startDate = salesDateRange.startDate.toISOString().split('T')[0];
      const endDate = salesDateRange.endDate.toISOString().split('T')[0];
      
      const salesData = await getSales(startDate, endDate);
      setSales(salesData);
    } catch (err) {
      console.error('Error fetching sales:', err);
      alert('Error al cargar las ventas: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSalesLoading(false);
    }
  };

  const handleShowSales = async () => {
    setShowSalesModal(true);
    await fetchSales();
  };

  useEffect(() => {
    fetchMetrics();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-gray-600">Cargando dashboard...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-message">
        <p>{error}</p>
        <button onClick={fetchMetrics} className="btn-primary mt-2">
          Reintentar
        </button>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">No hay datos disponibles</p>
      </div>
    );
  }

  const salesGrowth = metrics.monthly_comparison?.sales_growth || 0;
  const expenseGrowth = metrics.monthly_comparison?.expense_growth || 0;
  const salesTrend = getTrendIcon(salesGrowth);
  const expensesTrend = getTrendIcon(expenseGrowth);

  // Label showing the comparison period, e.g. "vs 1–7 mar"
  const comparisonLabel = (() => {
    const mc = metrics.monthly_comparison;
    if (!mc?.prev_period_start || !mc?.prev_period_end) return 'vs mes ant.';
    const fmt = (iso) => {
      const d = new Date(iso + 'T12:00:00');
      return d.toLocaleDateString('es-VE', { day: 'numeric', month: 'short' });
    };
    const days = mc.comparison_days;
    if (days === 1) return `vs ${fmt(mc.prev_period_start)}`;
    return `vs ${fmt(mc.prev_period_start)}–${fmt(mc.prev_period_end)}`;
  })();

  // Chart data
  const monthlyComparisonData = [
    {
      name: 'Mes Anterior',
      ventas: metrics.monthly_comparison?.previous_sales || 0,
      gastos: metrics.monthly_comparison?.previous_expenses || 0,
      compras: metrics.monthly_comparison?.previous_inventory_purchases || 0,
    },
    {
      name: 'Mes Actual',
      ventas: metrics.monthly_comparison?.current_sales || 0,
      gastos: metrics.monthly_comparison?.current_expenses || 0,
      compras: metrics.monthly_comparison?.current_inventory_purchases || 0,
    },
  ];

  const profitData = [
    { name: 'Ganancia Neta', value: metrics.net_profit, color: '#10B981' },
    { name: 'Gastos Operativos', value: metrics.total_expenses, color: '#EF4444' },
    { name: 'Compras Inventario', value: metrics.total_inventory_purchases || 0, color: '#F97316' },
  ];

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <img 
            src={logoUrl || "/farmavida-logo.jpg"} 
            alt={companyName} 
            className="h-16 w-auto object-contain rounded-lg shadow-sm"
          />
          <div>
            <h1 className="text-3xl font-bold text-blue-900">{`Dashboard ${companyName}`}</h1>
            <p className="text-gray-600 mt-1">
              {isAdmin 
                ? `Vista de Administrador - Resumen ejecutivo de tu negocio` 
                : `Vista de Empleado - Bienvenido, ${user?.full_name || user?.username}`
              }
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          {/* User Info Badge */}
          <div className="bg-white rounded-lg shadow-sm px-4 py-2 border">
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${isAdmin ? 'bg-red-500' : 'bg-green-500'}`}></div>
              <span className="text-sm font-medium text-gray-700">
                {user?.full_name || user?.username}
              </span>
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                isAdmin 
                  ? 'bg-red-100 text-red-800' 
                  : 'bg-green-100 text-green-800'
              }`}>
                {isAdmin ? 'Admin' : 'Empleado'}
              </span>
            </div>
          </div>
          <button
            onClick={handleShowSales}
            className="btn-primary flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>Ver Ventas</span>
          </button>
          <button
            onClick={fetchMetrics}
            className="btn-outline flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>Actualizar</span>
          </button>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Sales */}
        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div className="stat-card-icon bg-blue-500">
              💰
            </div>
            <div className={`stat-card-change ${salesGrowth >= 0 ? 'positive' : 'negative'}`}>
              <span className="flex flex-col items-end">
                <span className="flex items-center">{salesTrend.icon} {formatPercentage(Math.abs(salesGrowth))}</span>
                <span className="text-xs text-gray-400 font-normal">{comparisonLabel}</span>
              </span>
            </div>
          </div>
          <div className="stat-card-value">{formatDualCurrency(metrics.total_sales, exchangeRate, { compact: true })}</div>
          <div className="stat-card-label">Ventas Totales</div>
        </div>

        {/* Cost of Goods Sold - Costo de lo Vendido */}
        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div className="stat-card-icon bg-orange-500">
              📦
            </div>
            <div className="text-xs text-gray-500">
              {metrics.total_sales > 0 ? formatPercentage((metrics.cost_of_goods_sold / metrics.total_sales) * 100) : '0%'} de ventas
            </div>
          </div>
          <div className="stat-card-value">{formatDualCurrency(metrics.cost_of_goods_sold || 0, exchangeRate, { compact: true })}</div>
          <div className="stat-card-label">Costo de lo Vendido</div>
        </div>

        {/* Total Expenses - Gastos Operativos */}
        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div className="stat-card-icon bg-red-500">
              📊
            </div>
            <div className={`stat-card-change ${expenseGrowth <= 0 ? 'positive' : 'negative'}`}>
              <span className="flex flex-col items-end">
                <span className="flex items-center">{expensesTrend.icon} {formatPercentage(Math.abs(expenseGrowth))}</span>
                <span className="text-xs text-gray-400 font-normal">{comparisonLabel}</span>
              </span>
            </div>
          </div>
          <div className="stat-card-value">{formatDualCurrency(metrics.total_expenses, exchangeRate, { compact: true })}</div>
          <div className="stat-card-label">Gastos Operativos</div>
        </div>

        {/* Inventory Purchases */}
        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div className="stat-card-icon bg-orange-500">
              🛒
            </div>
            <div className="text-xs text-gray-500">
              Compras del período
            </div>
          </div>
          <div className="stat-card-value">{formatDualCurrency(metrics.total_inventory_purchases || 0, exchangeRate, { compact: true })}</div>
          <div className="stat-card-label">Compras Inventario</div>
        </div>

        {/* Net Profit */}
        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div className="stat-card-icon bg-green-500">
              📈
            </div>
            <div className="text-xs text-gray-500">
              Margen: {formatPercentage((metrics.net_profit / metrics.total_sales) * 100 || 0)}
            </div>
          </div>
          <div className="stat-card-value">{formatDualCurrency(metrics.net_profit, exchangeRate, { compact: true })}</div>
          <div className="stat-card-label">Ganancia Neta</div>
        </div>

        {/* Total Stores */}
        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div className="stat-card-icon bg-purple-500">
              🏪
            </div>
            {metrics.best_performing_store && (
              <div className="text-xs text-gray-500 text-center">
                <div>Mejor tienda:</div>
                <div className="font-medium">{metrics.best_performing_store.store_name}</div>
              </div>
            )}
          </div>
          <div className="stat-card-value">{metrics.total_stores}</div>
          <div className="stat-card-label">Tiendas Activas</div>
        </div>
      </div>

      {/* Daily Sales Comparison Chart */}
      <DailySalesChart token={localStorage.getItem('farmavida_token')} apiBase="/api" />

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Comparison Chart */}
        <div className="chart-container">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Comparación Mensual</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart 
              data={monthlyComparisonData}
              margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis 
                tickFormatter={(value) => formatDualCurrency(value, exchangeRate, { showBoth: false, primary: 'USD' })} 
                width={70}
              />
              <Tooltip 
                formatter={(value) => [formatDualCurrency(value, exchangeRate), '']}
                labelStyle={{ color: '#374151' }}
              />
              <Bar dataKey="ventas" fill="#3B82F6" name="Ventas" />
              <Bar dataKey="gastos" fill="#EF4444" name="Gastos Operativos" />
              <Bar dataKey="compras" fill="#F97316" name="Compras Inventario" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Profit Distribution */}
        <div className="chart-container">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Distribución de Ingresos</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={profitData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                dataKey="value"
                startAngle={90}
                endAngle={450}
              >
                {profitData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => formatDualCurrency(value, exchangeRate)} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center space-x-6 mt-4">
            {profitData.map((item) => (
              <div key={item.name} className="flex items-center space-x-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: item.color }}
                ></div>
                <span className="text-sm text-gray-600">{item.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      {metrics.best_performing_store && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">🏆 Tienda Destacada del Mes</h3>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-xl font-bold text-gray-900">
                {metrics.best_performing_store.store_name}
              </h4>
              <p className="text-gray-600">
                Ventas: {formatDualCurrency(metrics.best_performing_store.sales_amount, exchangeRate, { compact: true })}
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl">🎯</div>
              <p className="text-sm text-gray-500 mt-1">¡Excelente rendimiento!</p>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards - Enhanced for Admin */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card text-center">
          <div className="text-3xl mb-2">💡</div>
          <h4 className="font-semibold text-gray-900 mb-2">Ganancia Bruta</h4>
          <p className="text-2xl font-bold text-blue-600">{formatDualCurrency(metrics.gross_profit, exchangeRate, { compact: true })}</p>
          <p className="text-sm text-gray-500 mt-1">Total de ventas sin descuentos</p>
          {metrics.total_sales > 0 && (
            <p className="text-sm font-semibold text-blue-500 mt-1">
              {formatPercentage((metrics.gross_profit / metrics.total_sales) * 100)} de ventas totales
            </p>
          )}
        </div>

        <div className="card text-center">
          <div className="text-3xl mb-2">🎯</div>
          <h4 className="font-semibold text-gray-900 mb-2">Eficiencia</h4>
          <p className="text-2xl font-bold text-green-600">
            {formatPercentage((metrics.net_profit / metrics.total_sales) * 100 || 0)}
          </p>
          <p className="text-sm text-gray-500 mt-1">Margen de ganancia neta</p>
        </div>

        <div className="card text-center">
          <div className="text-3xl mb-2">📊</div>
          <h4 className="font-semibold text-gray-900 mb-2">Balance</h4>
          <p className={`text-2xl font-bold ${metrics.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {metrics.net_profit >= 0 ? '✅ Positivo' : '❌ Negativo'}
          </p>
          <p className="text-sm text-gray-500 mt-1">Estado financiero general</p>
        </div>
      </div>

      {/* Admin-Only Management Section */}
      {isAdmin && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">🔧 Panel de Administración</h3>
            <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">Solo Admin</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
              <div className="text-2xl mb-2">👥</div>
              <div className="text-lg font-bold text-blue-800">Control Total</div>
              <div className="text-sm text-blue-600">Gestión completa del sistema</div>
            </div>
            <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg">
              <div className="text-2xl mb-2">💰</div>
              <div className="text-lg font-bold text-green-800">Finanzas</div>
              <div className="text-sm text-green-600">Acceso a todos los datos financieros</div>
            </div>
            <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg">
              <div className="text-2xl mb-2">📊</div>
              <div className="text-lg font-bold text-purple-800">Reportes</div>
              <div className="text-sm text-purple-600">Reportes detallados y analíticas</div>
            </div>
            <div className="text-center p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg">
              <div className="text-2xl mb-2">⚙️</div>
              <div className="text-lg font-bold text-orange-800">Configuración</div>
              <div className="text-sm text-orange-600">Gestión de usuarios y tiendas</div>
            </div>
          </div>
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="text-yellow-600">⚠️</div>
              <div className="text-sm text-yellow-800">
                <strong>Privilegios de Administrador:</strong> Tienes acceso completo a todas las funcionalidades del sistema, 
                incluyendo gestión de usuarios, configuración de tiendas, y datos financieros sensibles.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Employee-Only Quick Actions */}
      {!isAdmin && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">⚡ Acciones Rápidas</h3>
            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">Vista Empleado</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg text-center">
              <div className="text-2xl mb-2">💳</div>
              <div className="text-lg font-bold text-blue-800">Ventas</div>
              <div className="text-sm text-blue-600">Registrar ventas diarias</div>
            </div>
            <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg text-center">
              <div className="text-2xl mb-2">📦</div>
              <div className="text-lg font-bold text-green-800">Inventario</div>
              <div className="text-sm text-green-600">Consultar stock disponible</div>
            </div>
            <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg text-center">
              <div className="text-2xl mb-2">📊</div>
              <div className="text-lg font-bold text-purple-800">Reportes</div>
              <div className="text-sm text-purple-600">Ver reportes autorizados</div>
            </div>
          </div>
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="text-blue-600">ℹ️</div>
              <div className="text-sm text-blue-800">
                <strong>Cuenta de Empleado:</strong> Tienes acceso a las funcionalidades operativas del sistema. 
                Para funciones administrativas, contacta a tu supervisor.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sales Modal */}
      {showSalesModal && (
        <div className="modal-overlay" onClick={() => setShowSalesModal(false)}>
          <div className="modal-content max-w-6xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">💰 Ventas Registradas</h2>
              <button
                onClick={() => setShowSalesModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              {/* Date Range Filter */}
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                  <div>
                    <label className="form-label">Fecha de inicio</label>
                    <DatePicker
                      selected={salesDateRange.startDate}
                      onChange={(date) => setSalesDateRange(prev => ({ ...prev, startDate: date }))}
                      className="form-input w-full"
                      dateFormat="dd/MM/yyyy"
                    />
                  </div>
                  
                  <div>
                    <label className="form-label">Fecha de fin</label>
                    <DatePicker
                      selected={salesDateRange.endDate}
                      onChange={(date) => setSalesDateRange(prev => ({ ...prev, endDate: date }))}
                      className="form-input w-full"
                      dateFormat="dd/MM/yyyy"
                    />
                  </div>

                  <button
                    onClick={fetchSales}
                    className="btn-primary"
                    disabled={salesLoading}
                  >
                    {salesLoading ? 'Cargando...' : '🔍 Filtrar'}
                  </button>
                </div>
              </div>

              {/* Sales Summary */}
              {sales.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="text-sm text-blue-600">Total de Ventas</div>
                    <div className="text-lg font-bold text-blue-800">{sales.length}</div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="text-sm text-green-600">Total USD</div>
                    <div className="text-lg font-bold text-green-800">
                      ${sales.reduce((sum, sale) => sum + (sale.amount_usd || 0), 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <div className="text-sm text-purple-600">Total VES</div>
                    <div className="text-lg font-bold text-purple-800">
                      Bs. {sales.reduce((sum, sale) => sum + (sale.amount_ves || 0), 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <div className="text-sm text-orange-600">Ganancia Total</div>
                    <div className="text-lg font-bold text-orange-800">
                      ${sales.reduce((sum, sale) => sum + (sale.profit_usd || 0), 0).toFixed(2)}
                    </div>
                  </div>
                </div>
              )}

              {/* Sales Table */}
              {salesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="loading-spinner"></div>
                  <span className="ml-2 text-gray-600">Cargando ventas...</span>
                </div>
              ) : sales.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-4xl mb-4">📋</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No hay ventas registradas
                  </h3>
                  <p className="text-gray-600">
                    No se encontraron ventas para el período seleccionado.
                  </p>
                </div>
              ) : (
                <div className="table-container max-h-96 overflow-y-auto">
                  <table className="table">
                    <thead className="sticky top-0 bg-white">
                      <tr>
                        <th>Fecha</th>
                        <th>Tienda</th>
                        <th>Descripción</th>
                        <th>Monto USD</th>
                        <th>Monto VES</th>
                        <th>Costo USD</th>
                        <th>Costo VES</th>
                        <th>Ganancia USD</th>
                        <th>Ganancia VES</th>
                        <th>Margen %</th>
                        <th>Transacciones</th>
                        <th>Ticket Promedio</th>
                        <th>Pagos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sales.map((sale) => (
                        <tr key={sale.id}>
                          <td>
                            <span className="text-sm font-mono">
                              {formatDate(sale.sale_date || sale.date)}
                            </span>
                          </td>
                          <td>
                            <span className="text-sm">
                              {sale.store_name || 'N/A'}
                            </span>
                          </td>
                          <td>
                            <span className="text-sm" title={sale.description}>
                              {sale.description?.length > 30 
                                ? `${sale.description.substring(0, 30)}...` 
                                : sale.description || 'N/A'}
                            </span>
                          </td>
                          <td>
                            <span className="font-semibold text-green-600">
                              ${(sale.amount_usd || 0).toFixed(2)}
                            </span>
                          </td>
                          <td>
                            <span className="font-semibold text-green-600">
                              Bs. {(sale.amount_ves || 0).toFixed(2)}
                            </span>
                          </td>
                          <td>
                            <span className="font-semibold text-red-600">
                              ${(sale.cost_of_goods_sold_usd || 0).toFixed(2)}
                            </span>
                          </td>
                          <td>
                            <span className="font-semibold text-red-600">
                              Bs. {(sale.cost_of_goods_sold_ves || 0).toFixed(2)}
                            </span>
                          </td>
                          <td>
                            <span className="font-semibold text-blue-600">
                              ${(sale.profit_usd || 0).toFixed(2)}
                            </span>
                          </td>
                          <td>
                            <span className="font-semibold text-blue-600">
                              Bs. {(sale.profit_ves || 0).toFixed(2)}
                            </span>
                          </td>
                          <td>
                            <span className={`font-semibold ${
                              (sale.profit_margin_percentage || 0) > 20 
                                ? 'text-green-600' 
                                : (sale.profit_margin_percentage || 0) > 10 
                                  ? 'text-yellow-600' 
                                  : 'text-red-600'
                            }`}>
                              {(sale.profit_margin_percentage || 0).toFixed(1)}%
                            </span>
                          </td>
                          <td>
                            <span className="text-sm text-center block">
                              {sale.daily_transactions || 1}
                            </span>
                          </td>
                          <td>
                            <div className="text-sm">
                              <div>${(sale.average_ticket_usd || 0).toFixed(2)}</div>
                              <div className="text-xs text-gray-500">
                                Bs. {(sale.average_ticket_ves || 0).toFixed(2)}
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="text-xs">
                              {sale.payment_breakdown && (
                                <div className="space-y-1">
                                  {sale.payment_breakdown.cash_usd > 0 && (
                                    <div>💵 ${sale.payment_breakdown.cash_usd.toFixed(2)}</div>
                                  )}
                                  {sale.payment_breakdown.cash_ves > 0 && (
                                    <div>💸 Bs. {sale.payment_breakdown.cash_ves.toFixed(2)}</div>
                                  )}
                                  {sale.payment_breakdown.zelle_transfer > 0 && (
                                    <div>💳 ${sale.payment_breakdown.zelle_transfer.toFixed(2)}</div>
                                  )}
                                  {(sale.payment_breakdown.pos_banesco + 
                                    sale.payment_breakdown.pos_bdv + 
                                    sale.payment_breakdown.pos_banplus + 
                                    sale.payment_breakdown.pos_provincial) > 0 && (
                                    <div>🏦 POS</div>
                                  )}
                                  {(sale.payment_breakdown.pago_movil_banesco + 
                                    sale.payment_breakdown.pago_movil_bdv + 
                                    sale.payment_breakdown.pago_movil_banplus) > 0 && (
                                    <div>📱 Móvil</div>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowSalesModal(false)}
                className="btn-outline"
              >
                Cerrar
              </button>
              {sales.length > 0 && (
                <button
                  onClick={() => {
                    // Export functionality could be added here
                    const csvData = sales.map(sale => ({
                      Fecha: sale.sale_date || sale.date,
                      Tienda: sale.store_name || 'N/A',
                      Descripción: sale.description || 'N/A',
                      'Monto USD': sale.amount_usd || 0,
                      'Monto VES': sale.amount_ves || 0,
                      'Costo USD': sale.cost_of_goods_sold_usd || 0,
                      'Costo VES': sale.cost_of_goods_sold_ves || 0,
                      'Ganancia USD': sale.profit_usd || 0,
                      'Ganancia VES': sale.profit_ves || 0,
                      'Margen %': sale.profit_margin_percentage || 0,
                      Transacciones: sale.daily_transactions || 1
                    }));
                    
                    console.log('Export data:', csvData);
                    alert('Funcionalidad de exportación disponible en consola');
                  }}
                  className="btn-primary"
                >
                  📤 Exportar
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;